import { groupBy, keyBy, mapValues, partition, set, uniq, uniqBy } from 'lodash/fp';

import type {
  CombinedRunStepForTree,
  CombinedStep,
  PreviewPageMap,
  StepPreviewMap,
  TreeNode,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  Edge,
  extractActionsAndConditionalNodes,
  mapStepsPreviewAndToken,
  TraverseArgs,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import {
  firstInitialStep,
  uiStepAllComponentConfigs,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import { Event } from 'dashboard/models/event';
import { InquiryEvent } from 'dashboard/models/inquiry';
import { InquiryTemplateVersionStep } from 'dashboard/models/inquiry-template-version';
import { WorkflowStepType } from 'dashboard/models/workflow-version';
import { RunStepConfig, RunStepTreeConfig } from 'dashboard/redux/slices/inquiry-run';
import { EventName } from 'lib/constants/events';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import { isUiTransitionComponentConfig } from 'lib/interfaces/ui-components';
import { assertNever } from 'lib/utils/types';

export const inquiryEventsLoaded = (events: Event[], inquiryId: string): boolean => {
  return (events ?? []).some((event) => {
    const { name, payload } = event.attributes;
    if (name === EventName.InquiryTransitioned) {
      const inquiryEventData = payload.data as InquiryEvent;
      return inquiryEventData.id === inquiryId;
    } else {
      return false;
    }
  });
};

/**
 * Finds all occurrences of chosen conditional run steps matching the provided token
 * and returns a unique (via the config's chosenIndex) array of the configs
 * + their indexes within the original array
 *
 * This helper allows a tree to be constructed from the Inquiry run steps which handles retries
 * and is then passed to buildScreenTreeAndGraph() to highlight the correct nodes and branch indexes
 *
 * As a potential next enhancement, this logic could track configs with duplicate chosenIndex values and
 * return a retryCount to be rendered over the connectors of the Inquiry run tree
 */
const getBranchConfigsWithToken = (
  token: string,
  runStepConfigs: RunStepConfig[],
): { config: RunStepConfig; configIndex: number }[] => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return uniqBy(
    'config.chosenIndex',
    runStepConfigs
      .map((config, configIndex) =>
        config.token === token
          ? {
              config,
              configIndex,
            }
          : null,
      )
      .filter(Boolean),
  );
};

/**
 * Transforms linear runStepConfigs derived from Inquiry run events into tree form
 * to correctly set included run path nodes in buildScreenTreeAndGraph().
 *
 * Finds the next chosen conditional run step, then all remaining occurrences of it, and
 * constructs chosenPathMap via the returned indexes. The non-last elements should represent
 * retries, and the last element should be the 'continuation' along the run path
 */
export const getRunStepTreeConfigs = (runStepConfigs: RunStepConfig[]): RunStepTreeConfig[] => {
  const forwardRunStepConfigs = runStepConfigs.filter(({ isTransitionBack }) => !isTransitionBack);
  const config = forwardRunStepConfigs.find((config) => config.chosenIndex != null);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const branchConfigs = getBranchConfigsWithToken(config?.token, runStepConfigs);
  const runStepTreeConfigs: RunStepTreeConfig[] = runStepConfigs.map(({ token, prevToken }) => ({
    token,
    prevToken,
  }));
  if (branchConfigs.length === 0) {
    return uniqBy('token', runStepTreeConfigs);
  } else {
    const branchStepConfig: RunStepTreeConfig = {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      token: config.token,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      prevToken: config.prevToken,
      chosenPathMap: branchConfigs.reduce(
        (map, { config, configIndex }, idx) => ({
          ...map,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          [config.chosenIndex]:
            idx === branchConfigs.length - 1
              ? getRunStepTreeConfigs(runStepConfigs.slice(configIndex + 1))
              : uniqBy(
                  'token',
                  runStepTreeConfigs.slice(configIndex + 1, branchConfigs[idx + 1].configIndex),
                ),
        }),
        {},
      ),
    };
    return uniqBy('token', runStepTreeConfigs.slice(0, branchConfigs[0].configIndex)).concat(
      branchStepConfig,
    );
  }
};

/**
 * Builds a map of steps with parent and child relations.
 */
export const buildRunGraph = (
  stepsToTraverse: CombinedStep[],
  initialStepName: string,
): Record<string, TreeNode> => {
  const stepMap: Record<string, CombinedStep> = keyBy('token', stepsToTraverse);

  // Generate edges for establishing step hierarchy
  const { edges } = traverse(stepMap)({
    visitedMap: mapValues(() => false, stepMap),
    stepsToTraverse,
    edges: [],
  });

  const tokensToNode = stepsToTraverse.reduce<Record<string, TreeNode>>((map, step) => {
    map[step.token] = {
      step,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      parent: null,
      retryParents: [],
      children: [],
    };
    return map;
  }, {});

  const sortedEdges = sortEdges(edges, initialStepName);
  // Add parent and child relationships via edges
  sortedEdges.forEach(({ target, source }) => {
    // skip if target or source not found in tokensToNode
    if (!tokensToNode[source]) {
      return;
    }
    // for inquiry loop steps, we need to recursively process all targets
    if (!tokensToNode[target]) {
      const processedNodes = new Set<string>();

      const processTargets = (currentTarget: string) => {
        if (processedNodes.has(currentTarget)) {
          return;
        }
        processedNodes.add(currentTarget);

        const targets = edges.filter((edge) => edge.source === currentTarget);
        targets.forEach(({ target: newTarget, source: _ }) => {
          if (tokensToNode[newTarget]) {
            tokensToNode[newTarget].parent = source;
            tokensToNode[source].children.push(newTarget);
            processTargets(newTarget);
          }
        });
      };
      processTargets(target);
    } else {
      tokensToNode[target].parent = source;
      tokensToNode[source].children.push(target);
    }
  });
  return tokensToNode;
};

/**
 * Does a breadth-first traversal of the graph to build nodes and edges.
 */
export const traverse = (stepMap: Record<string, CombinedStep>) => {
  const traverseRecursive = ({
    visitedMap,
    stepsToTraverse,
    edges,
  }: TraverseArgs): TraverseArgs => {
    // Base case: return from recursion
    if (stepsToTraverse.length === 0) {
      return {
        visitedMap,
        stepsToTraverse,
        edges,
      };
    }

    const [curr, ...restSteps] = stepsToTraverse;

    // This graph may be cyclic. Ignore revisited steps.
    if (visitedMap[curr.token]) {
      return traverseRecursive({
        visitedMap,
        stepsToTraverse: restSteps,
        edges,
      });
    }

    const newEdges: Edge[] = [];
    switch (curr.type) {
      case UiStepType.Complete:
        // complete is a non-UI terminal step that will never transition to another
        break;
      case UiStepType.Loop: {
        const loopSteps = mapStepsPreviewAndToken({
          steps: curr.config.steps,
          stepsPreviewMap: {},
          previewPageMap: {},
        });
        newEdges.push(
          ...loopSteps.map((step) => ({
            source: curr.token,
            target: step.token,
          })),
        );
        return traverseRecursive({
          visitedMap: set(curr.token, true, visitedMap),
          stepsToTraverse: restSteps.concat(loopSteps),
          edges: edges.concat(newEdges),
        });
        break;
      }
      case UiStepType.Ui: {
        // For every component that can trigger a transition, add an edge.
        uiStepAllComponentConfigs(curr)
          .filter(isUiTransitionComponentConfig)
          .forEach((component) => {
            if (component.config['to-step']) {
              newEdges.push({
                source: curr.token,
                target: component.config['to-step'],
              });
              // should always be mutually exclusive
            } else if (component.config['workflow-version-id']) {
              newEdges.push({
                source: curr.token,
                target: component.config['workflow-version-id'],
              });
              if (component.config['workflow-on-error-step-name']) {
                newEdges.push({
                  source: curr.token,
                  target: component.config['workflow-on-error-step-name'],
                });
              }
            }
          });
        break;
      }
      case UiStepType.Document:
      case UiStepType.Integration:
      case UiStepType.VerifyWithPersona:
      case UiStepType.Selfie:
      case UiStepType.SelfieReflection:
      case UiStepType.GovernmentId: {
        if (curr.config['to-step']) {
          newEdges.push({
            source: curr.token,
            target: curr.config['to-step'],
          });
          // should always be mutually exclusive
        } else if (curr.config['workflow-version-id']) {
          newEdges.push({
            source: curr.token,
            target: curr.config['workflow-version-id'],
          });
          if (curr.config['workflow-on-error-step-name']) {
            newEdges.push({
              source: curr.token,
              target: curr.config['workflow-on-error-step-name'],
            });
          }
        }
        break;
      }
      case 'workflow': {
        newEdges.push(
          ...curr.nextSteps.map((nextStep) => ({
            source: curr.token,
            target: nextStep,
          })),
        );
        break;
      }
      default:
        assertNever(curr);
    }

    // Check for edges linked to nodes that do not exist
    // This implies that a step was removed without updating it s parent
    const [_orphanEdges, existingNewEdges] = partition(
      (edge) => stepMap[edge.target] == null,
      newEdges,
    );
    // Forbid self cycles
    const [_selfCycleEdges, validNewEdges] = partition(
      (edge) => edge.target === edge.source,
      existingNewEdges,
    );

    const newStepsToTraverse = validNewEdges.map((edge) => stepMap[edge.target]);

    return traverseRecursive({
      visitedMap: set(curr.token, true, visitedMap),
      stepsToTraverse: restSteps.concat(newStepsToTraverse),
      edges: edges.concat(newEdges),
    });
  };

  return traverseRecursive;
};

/**
 * Given a list of edges, topologically sorts them while using initial source
 * as the start edge.
 */
export function sortEdges(edges: Edge[], initialSource: string): Edge[] {
  const visited = new Set<Edge>();
  const stack: Edge[] = [];
  const edgeMap = groupBy((edge) => edge.source, edges);

  /**
   * Recursive topological sort helper
   */
  const topoSortHelper = (edge: Edge, visited: Set<Edge>, stack: Edge[]) => {
    visited.add(edge);
    stack.push(edge);

    // will be empty if target edge is a terminal edge
    if (edgeMap[edge.target] != null) {
      edgeMap[edge.target].forEach((targetEdge) => {
        if (!visited.has(targetEdge)) {
          topoSortHelper(targetEdge, visited, stack);
        }
      });
    }
  };

  // There may be more than one edge where source is the initial step
  const [initialEdges, restEdges] = partition((edge) => edge.source === initialSource, edges);

  // Place initial edges at start to ensure toposort has right origin
  initialEdges.concat(restEdges).forEach((edge) => {
    if (!visited.has(edge)) {
      topoSortHelper(edge, visited, stack);
    }
  });

  return stack;
}

const buildRunTree = (
  step: CombinedStep,
  graph: Record<string, TreeNode>,
  runStepTreeConfigs?: RunStepTreeConfig[],
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
) => buildRunTreeRecursive(new Set<string>(), step, graph, runStepTreeConfigs);

/**
 * Builds tree from ITV/workflow steps while distinguishing steps/paths to be highlighted
 * in order to visually represent the path that an end user took through the Inquiry flow.
 * Notes:
 * 1. uses visited Set which is mutated to ensure visited steps return only themselves
 *    upon subsequent visits rather than duplicating the subtree.
 *    (needed for rendering connectors, which are visual representations of step pointers)
 * 2. omits update_next_step_actions as these are now implicitly represented by branching node connectors
 * 3. adds support for branches originating from non-workflow steps
 *    (screens with multiple buttons or workflow_on_error_step_name)
 *
 * @returns combined screen + workflow run steps with highlight/omit values used to render InquiryRunTree
 */
const buildRunTreeRecursive = (
  visited: Set<string>,
  step: CombinedStep,
  graph: Record<string, TreeNode>,
  runStepTreeConfigs: RunStepTreeConfig[],
): CombinedRunStepForTree[] => {
  const runStepTreeConfig = runStepTreeConfigs?.[0];
  /**
   * we use includes rather than matching here to account wfv nodes in the run path
   * which are the 'initial' node, as those have the wfv token set as their token
   */
  const isInRunPath = runStepTreeConfig?.token?.includes(step.token);
  const chosenIndexes = Object.keys(runStepTreeConfig?.chosenPathMap ?? {}).map((index) =>
    Number(index),
  );

  /**
   * return just the current step if step has already been visited.
   * avoids duplicate subtrees, instead renders a reference to an existing screen (connector)
   */
  if (visited.has(step.token)) {
    return [
      {
        ...step,
        isConnector: true,
        isInRunPath,
      },
    ];
  }

  visited.add(step.token);
  const hasBranches = graph[step.token].children.length > 1;

  /**
   * UiStepType.Complete is a non-UI terminal step. For all other step types, return:
   * - step + recursive remainder of branch OR
   * - step with branches (+recursive branch paths)
   */
  switch (step.type) {
    case UiStepType.Complete:
      return [{ ...step, isInRunPath }];
    case UiStepType.Loop:
    case UiStepType.Document:
    case UiStepType.Integration:
    case UiStepType.VerifyWithPersona:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
    case UiStepType.GovernmentId:
    case UiStepType.Ui: {
      /**
       * Construct chosenPathMap in-place here rather than in getRunStepTreeConfigs() because
       * screen step branching is often non-sequential and can lead to transition events being
       * dropped or out of order (examples: KYB flows, Document home)
       */
      const chosenPathMap: Record<number, { tokenIndex: number; prevToken: string }> = uniq(
        graph[step.token].children,
      ).reduce((map, child, i) => {
        const tokenIndex = (runStepTreeConfigs ?? []).findIndex(
          (config) =>
            config.token.includes(graph[child].step.token) &&
            // pass configs if current step directly transitions to a subsequent config (child)
            // OR if a subsequent config exists on the remaining path (connector reference)
            (config.prevToken.includes(step.token) || !visited.has(graph[child].step.token)),
        );
        return tokenIndex !== -1
          ? {
              ...map,
              [i]: {
                tokenIndex,
                prevToken: runStepTreeConfigs[tokenIndex].prevToken,
              },
            }
          : map;
      }, {});
      /**
       * filter indexes that do not directly continue from the current step, as
       * the connector should not be highlighted even if the subsequent step
       * is on the run path (e.g reached via a connector)
       */
      const chosenIndexes = Object.keys(chosenPathMap)
        .map((index) => {
          const { tokenIndex, prevToken } = chosenPathMap[Number(index)];
          return tokenIndex === 1 || prevToken.includes(step.token) ? Number(index) : null;
        })
        .filter((val): val is number => val != null);
      /**
       * branches for UI steps occur when:
       * - the step has multiple buttons OR
       * - a button on the step transitions to a workflow, as the
       *   workflow_on_error_step_name attribute is then required
       */
      return hasBranches
        ? [
            {
              ...step,
              chosenIndexes,
              // set isInRunPath to true if chosen indexes are found in case this step's
              // transition event was dropped, but subsequent valid runStepConfigs exist
              isInRunPath: isInRunPath || chosenIndexes.length > 0,
              branches: graph[step.token].children.map((node, i) => {
                const tokenIndex = chosenPathMap[i]?.tokenIndex;
                return buildRunTreeRecursive(
                  visited,
                  graph[node].step,
                  graph,
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  tokenIndex != null ? runStepTreeConfigs?.slice(tokenIndex) : null,
                );
              }),
            },
          ]
        : [
            // set isInRunPath to true if subsequent runStepConfigs remain in case this step's
            // transition event was dropped. only slice array if step is confirmed to be on run path
            {
              ...step,
              isInRunPath: isInRunPath || runStepTreeConfigs?.length > 0,
            },
            ...graph[step.token].children.flatMap((node) =>
              buildRunTreeRecursive(
                visited,
                graph[node].step,
                graph,
                isInRunPath ? runStepTreeConfigs?.slice(1) : runStepTreeConfigs,
              ),
            ),
          ];
    }
    case 'workflow': {
      /**
       * workflowStepType is added to the node for stronger typing.
       * update_next_step actions are omitted, as these aren't rendered in the InquiryRunTree.
       */
      const currentStep = {
        ...step,
        workflowStepType: step.workflowStep?.type,
      };
      const skipCurrentStep = step.workflowStep.isUpdateNextStepAction;
      return step.workflowStep?.type === WorkflowStepType.Conditional
        ? [
            {
              ...currentStep,
              // set isInRunPath to true if chosen indexes are found in case this step's
              // transition event was dropped, but subsequent valid runStepConfigs exist
              isInRunPath: isInRunPath || chosenIndexes?.length > 0,
              chosenIndexes,
              // map with step.workflowStep.branches over graph[step.token].children to support empty branches
              branches: step.nextSteps.map(
                (nextStepToken, i) =>
                  nextStepToken != null
                    ? buildRunTreeRecursive(
                        visited,
                        graph[nextStepToken].step,
                        graph,
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        runStepTreeConfig?.chosenPathMap?.[i],
                      )
                    : [], // still want to display a branch even if its empty
              ),
            },
          ]
        : [
            ...(skipCurrentStep
              ? []
              : /**
                 * actions nodes must be in the run path if runStepTreeConfigs is non-empty because
                 * even though they are not explicitly identified by Inquiry run events,
                 * the prev/next nodes must be screen or branch nodes which are.
                 */
                [{ ...currentStep, isInRunPath: runStepTreeConfigs?.length > 0 }]),
            ...graph[step.token].children.flatMap((node) =>
              buildRunTreeRecursive(visited, graph[node].step, graph, runStepTreeConfigs),
            ),
          ];
    }
    default:
      assertNever(step);
  }
};

/**
 * @param steps server representation of ITV steps data
 * @param stepsPreview client data representation of ITV steps
 * @param pages preview component to render for each step in tree, props populated via stepsPreview
 * @param workflowVersionMap workflow versions referenced in steps
 * @param runStepTreeConfigs configs used to highlight and omit steps and connectors
 */
type BuildInquiryRunTreeParams = {
  steps: InquiryTemplateVersionStep[];
  stepsPreviewMap: StepPreviewMap;
  previewPageMap: PreviewPageMap;
  workflowVersionMap: WorkflowVersionMap;
  runStepTreeConfigs: RunStepTreeConfig[];
};

export const buildInquiryRunTree = ({
  steps,
  stepsPreviewMap,
  previewPageMap,
  workflowVersionMap,
  runStepTreeConfigs,
}: BuildInquiryRunTreeParams): CombinedRunStepForTree[] => {
  const initialStep = firstInitialStep(steps);
  if (initialStep == null) {
    return [];
  }
  const combinedSteps = [
    ...mapStepsPreviewAndToken({ steps, stepsPreviewMap, previewPageMap }),
    ...extractActionsAndConditionalNodes(workflowVersionMap),
  ];
  return buildRunTree(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    combinedSteps.find((step) => step.token === initialStep.name),
    buildRunGraph(combinedSteps, initialStep.name),
    runStepTreeConfigs,
  );
};
